/**
 * The inventory, in IndexedDB, behind a synchronous front door.
 *
 * ## The shape of the problem
 *
 * IndexedDB is asynchronous and the entire app is not. `loadFoodItems()` is
 * called during render by half a dozen components, and the tests — 434 of them
 * — are built on it returning an array rather than a promise. Making it async
 * would mean touching every one of those, and every touch is a chance to break
 * something that currently works.
 *
 * So the async part happens **once**, at startup: the database is read into
 * memory before the app renders, and after that the cache *is* the source of
 * truth for reading. Writes update memory synchronously — so the next render
 * sees them, exactly as before — and persist in the background.
 *
 * ## What that trades away, honestly
 *
 * A write that fails after the fact cannot be reported at the call site, so a
 * persistence failure is surfaced through `onPersistError` instead of thrown.
 * The old code could throw a StorageError straight into the form that caused
 * it; this cannot, and the shell shows it as a page-level error instead.
 *
 * The upside is the one that matters: a full-quota failure was the *common*
 * case in localStorage and is a rounding error here.
 */

import { db, hasIndexedDb } from "./db";
import type { StoredItem } from "./db";
import { FOOD_CATEGORIES } from "../../features/food/types";
import type { FoodCategory, FoodItem } from "../../features/food/types";

/** Everything currently known, in memory. Read by every render. */
let cache: FoodItem[] = [];
let hydrated = false;

/** Told when a background write fails, so the shell can say so. */
let onPersistError: ((message: string) => void) | null = null;

export function reportPersistErrorsTo(handler: (message: string) => void): void {
  onPersistError = handler;
}

function failed(error: unknown): void {
  onPersistError?.(
    error instanceof Error && error.name === "QuotaExceededError"
      ? "Couldn't save — your browser storage is full."
      : "Couldn't save that to storage. Your change is on screen but may not survive a reload."
  );
}

/**
 * The category list changed once already (twelve became seven). Anything under
 * a name we no longer have becomes "other" rather than being dropped, so an old
 * inventory survives the upgrade intact.
 */
function readCategory(item: FoodItem): FoodItem {
  const known = (FOOD_CATEGORIES as readonly string[]).includes(item.category);
  return known ? item : { ...item, category: "other" as FoodCategory };
}

/** Splits an item for storage: the row in one place, the picture in another. */
function split(item: FoodItem): { row: StoredItem; photo: string | undefined } {
  const { photo, ...row } = item;
  return { row, photo };
}

/**
 * Reads everything into memory. Called once, before the app renders.
 *
 * Never rejects. IndexedDB is unavailable in some private-browsing modes, and
 * an app that refuses to start because it couldn't reach its cache would be a
 * far worse outcome than one that starts empty.
 */
export async function hydrateItems(): Promise<void> {
  hydrated = true;
  if (!hasIndexedDb()) return;

  try {
    const database = await db();
    const [rows, photos] = await Promise.all([
      database.getAll("items"),
      database.getAll("photos"),
    ]);

    const pictures = new Map(photos.map((entry) => [entry.id, entry.photo]));
    cache = rows.map((row) =>
      readCategory(pictures.has(row.id) ? { ...row, photo: pictures.get(row.id) } : row)
    );
  } catch {
    cache = [];
  }
}

/** True once hydration has run, whether or not it found anything. */
export function isHydrated(): boolean {
  return hydrated;
}

/** Everything, as the app has always expected it. */
export function loadFoodItems(): FoodItem[] {
  return cache.map(readCategory);
}

/** Replaces the lot. One transaction, so a half-written inventory is impossible. */
export function replaceFoodItems(items: FoodItem[]): void {
  cache = [...items];
  void persistAll(items);
}

async function persistAll(items: FoodItem[]): Promise<void> {
  if (!hasIndexedDb()) return;
  try {
    const database = await db();
    const tx = database.transaction(["items", "photos"], "readwrite");
    await Promise.all([tx.objectStore("items").clear(), tx.objectStore("photos").clear()]);
    for (const item of items) {
      const { row, photo } = split(item);
      await tx.objectStore("items").put(row);
      if (photo) await tx.objectStore("photos").put({ id: row.id, photo });
    }
    await tx.done;
  } catch (error) {
    failed(error);
  }
}

/** Adds an item, or replaces the one with the same id. */
export function saveFoodItem(item: FoodItem): void {
  const index = cache.findIndex((existing) => existing.id === item.id);
  if (index === -1) cache = [...cache, item];
  else cache = cache.map((existing, i) => (i === index ? item : existing));

  void persistOne(item);
}

async function persistOne(item: FoodItem): Promise<void> {
  if (!hasIndexedDb()) return;
  try {
    const database = await db();
    const { row, photo } = split(item);
    const tx = database.transaction(["items", "photos"], "readwrite");
    await tx.objectStore("items").put(row);
    // A photo removed in the edit sheet has to be removed here too, or the
    // rejoin at next startup would bring it back from the dead.
    if (photo) await tx.objectStore("photos").put({ id: row.id, photo });
    else await tx.objectStore("photos").delete(row.id);
    await tx.done;
  } catch (error) {
    failed(error);
  }
}

export function deleteFoodItem(id: string): void {
  cache = cache.filter((item) => item.id !== id);
  void forgetOne(id);
}

async function forgetOne(id: string): Promise<void> {
  if (!hasIndexedDb()) return;
  try {
    const database = await db();
    const tx = database.transaction(["items", "photos"], "readwrite");
    await tx.objectStore("items").delete(id);
    await tx.objectStore("photos").delete(id);
    await tx.done;
  } catch (error) {
    failed(error);
  }
}

/** Tests only: empties memory so each case starts from nothing. */
export function resetItemCacheForTests(items: FoodItem[] = []): void {
  cache = [...items];
  hydrated = true;
}
